export type DownsampleStrategy = "lttb" | "minmax";

export type XYPoint = {
  x: number;
  y: number;
};

export type DownsampleInput = {
  id: string;
  points: XYPoint[];
  version: string;
};

export type DownsampleParams = {
  targetPoints: number;
  strategy: DownsampleStrategy;
  viewport?: { start: number; end: number } | null;
};

// Cache results keyed by series/range/strategy so we only recompute when the
// dataset, viewport or density changes meaningfully.
export class DownsampleCache {
  private store = new Map<string, XYPoint[]>();

  get(key: string) {
    return this.store.get(key);
  }

  set(key: string, value: XYPoint[]) {
    this.store.set(key, value);
  }

  clearSeries(seriesId: string) {
    for (const key of this.store.keys()) {
      if (key.startsWith(`${seriesId}|`)) {
        this.store.delete(key);
      }
    }
  }
}

const clampTarget = (value: number) => Math.max(1, Math.floor(value));

function makeViewportKey(viewport: DownsampleParams["viewport"], targetPoints: number) {
  if (!viewport) return "full";
  const span = viewport.end - viewport.start;
  if (!Number.isFinite(span) || span <= 0) return "full";
  // Quantize to avoid recalculating on imperceptible pans/zooms
  const granularity = Math.max(1, Math.round(span / targetPoints));
  const startKey = Math.round(viewport.start / granularity);
  const endKey = Math.round(viewport.end / granularity);
  return `${startKey}-${endKey}`;
}

function makeKey(input: DownsampleInput, params: DownsampleParams) {
  const target = clampTarget(params.targetPoints);
  const viewportKey = makeViewportKey(params.viewport, target);
  return `${input.id}|${input.version}|${viewportKey}|${params.strategy}|${target}`;
}

function sliceRange(points: XYPoint[], viewport: DownsampleParams["viewport"]) {
  if (!viewport) return points;
  const { start, end } = viewport;
  let from = 0;
  let to = points.length;

  // lower bound for start
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].x < start) lo = mid + 1;
    else hi = mid;
  }
  from = lo;

  // upper bound for end
  lo = from;
  hi = points.length - 1;
  while (lo < hi) {
    const mid = 1 + ((lo + hi) >> 1);
    if (points[mid].x > end) hi = mid - 1;
    else lo = mid;
  }
  to = Math.min(points.length, hi + 1);

  return points.slice(from, to);
}

function largestTriangleThreeBuckets(points: XYPoint[], threshold: number): XYPoint[] {
  const n = points.length;
  if (threshold >= n || threshold <= 0) return points;

  const sampled: XYPoint[] = [points[0]];
  const every = (n - 2) / (threshold - 2);
  let a = 0;

  for (let i = 0; i < threshold - 2; i++) {
    let avgX = 0;
    let avgY = 0;
    const avgStart = Math.floor((i + 1) * every) + 1;
    const avgEnd = Math.floor((i + 2) * every) + 1;
    const avgTo = Math.min(avgEnd, n);
    const avgFrom = Math.min(avgStart, n - 1);
    const len = Math.max(1, avgTo - avgFrom);

    for (let j = avgFrom; j < avgTo; j++) {
      avgX += points[j].x;
      avgY += points[j].y;
    }
    avgX /= len;
    avgY /= len;

    const rangeOffs = Math.floor(i * every) + 1;
    const rangeTo = Math.floor((i + 1) * every) + 1;

    let maxArea = -1;
    let nextA = rangeOffs;

    for (let j = rangeOffs; j < rangeTo; j++) {
      const x0 = points[a].x;
      const y0 = points[a].y;
      const x1 = points[j].x;
      const y1 = points[j].y;
      const area = Math.abs((x0 - avgX) * (y1 - y0) - (x0 - x1) * (avgY - y0)) * 0.5;
      if (area > maxArea) {
        maxArea = area;
        nextA = j;
      }
    }

    sampled.push(points[nextA]);
    a = nextA;
  }

  sampled.push(points[n - 1]);
  return sampled;
}

function minMaxPerBucket(points: XYPoint[], target: number): XYPoint[] {
  if (points.length <= target) return points;
  const bucketSize = Math.max(1, Math.floor(points.length / target));
  const result: XYPoint[] = [points[0]];

  for (let i = 0; i < points.length; i += bucketSize) {
    const end = Math.min(points.length, i + bucketSize);
    let min = points[i];
    let max = points[i];
    for (let j = i; j < end; j++) {
      const p = points[j];
      if (p.y < min.y) min = p;
      if (p.y > max.y) max = p;
    }

    if (min === max) {
      if (result[result.length - 1] !== min) result.push(min);
      continue;
    }

    const first = min.x <= max.x ? min : max;
    const second = min.x <= max.x ? max : min;

    if (result[result.length - 1] !== first) result.push(first);
    result.push(second);
  }

  const last = points[points.length - 1];
  if (result[result.length - 1] !== last) {
    result.push(last);
  }

  return result;
}

export function downsampleSeries(
  input: DownsampleInput,
  params: DownsampleParams,
  cache: DownsampleCache,
): XYPoint[] {
  const key = makeKey(input, params);
  const cached = cache.get(key);
  if (cached) return cached;

  const target = clampTarget(params.targetPoints);
  const windowed = sliceRange(input.points, params.viewport);
  let output = windowed;

  if (windowed.length > target) {
    output =
      params.strategy === "minmax"
        ? minMaxPerBucket(windowed, target)
        : largestTriangleThreeBuckets(windowed, target);
  }

  cache.set(key, output);
  return output;
}

export function makeSeriesVersion(points: XYPoint[]) {
  if (!points.length) return "empty";
  const first = points[0]?.x ?? 0;
  const last = points[points.length - 1]?.x ?? 0;
  return `${points.length}:${first}:${last}`;
}
